import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Add as PlusIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { EventSummary } from '@common/schemas/entities/summary.schema';

import { SubscriptionLimitGate } from '@/components/subscription/subscription-limit-gate';
import { TimelineEventCard } from './timeline-event-card';

function SortableEventNode({
  event,
  index,
  y,
  isLeft,
  isDraggingNode,
  entityNameMap,
  onDelete,
  handleHeightChange,
  isDragDisabled
}: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: event.id });

  const style = {
    transform: isDraggingNode ? CSS.Translate.toString(transform) : undefined,
    transition: isDraggingNode
      ? transition || 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s',
    opacity: isDraggingNode ? 0.6 : 1,
    zIndex: isDraggingNode ? 100 : 1,
    position: 'absolute' as const,
    top: y,
    left: isLeft ? 0 : '50%',
    width: '50%',
    display: 'flex',
    justifyContent: isLeft ? 'flex-end' : 'flex-start',
    paddingRight: isLeft ? 48 : 0,
    paddingLeft: !isLeft ? 48 : 0,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <Box sx={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        <TimelineEventCard
          event={event}
          entityNameMap={entityNameMap}
          index={index}
          side={isLeft ? 'left' : 'right'}
          onDelete={onDelete}
          onHeightChange={handleHeightChange}
          dragHandleProps={isDragDisabled ? {} : { ...attributes, ...listeners }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 25.5,
          [isLeft ? 'right' : 'left']: 0,
          width: 48,
          height: '1px',
          bgcolor: 'primary.main',
          zIndex: 0,
        }}
      />
    </Box>
  );
}

interface EventsTimelineProps {
  events: EventSummary[];
  entityNameMap: Record<string, string>;
  novelId: string;
  onReorder: (newOrder: EventSummary[]) => void;
  onInsert: (order: number) => void;
  onDelete: (id: string) => void;
  isDragDisabled?: boolean;
}

/** Insert point that shows on hover between events on the spine */
function SpineInsertPoint({ insertOrder, onInsert, currentCount }: { insertOrder: number; onInsert: (o: number) => void; currentCount: number }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={e => { e.stopPropagation(); onInsert(insertOrder); }}
      sx={{
        height: 48,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 2,
        width: '100%',
      }}
    >
      {/* Dashed line across the spine */}
      <Fade in={hovered}>
        <Box sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '800px',
          maxWidth: '80vw',
          height: 0,
          borderTop: '2px dashed',
          borderColor: 'primary.main',
          opacity: 0.6,
          pointerEvents: 'none',
        }} />
      </Fade>

      <Fade in={hovered}>
        <Box sx={{ position: 'relative', zIndex: 3 }}>
          <SubscriptionLimitGate limit="eventsPerNovel" currentCount={currentCount}>
            <Tooltip title={t('entity_list.events.insert_here')} placement="top" arrow>
              <IconButton
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: '0 0 15px rgba(255, 152, 0, 0.4)',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.15) rotate(90deg)' },
                  transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                }}
              >
                <PlusIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </SubscriptionLimitGate>
        </Box>
      </Fade>
    </Box>
  );
}

/** Numbered dot on the spine */
function SpineDot({ order }: { order: number }) {
  return (
    <Box sx={{
      width: 32, height: 32, borderRadius: '50%',
      bgcolor: 'primary.main', color: 'primary.contrastText',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.72rem', fontWeight: 700,
      boxShadow: 4, flexShrink: 0, zIndex: 2, position: 'relative',
    }}>
      {order + 1}
    </Box>
  );
}

function calculatePositions(eventsArr: EventSummary[], heightsMap: Record<string, number>) {
  const pos: number[] = [];
  let leftBottom = 48;
  let rightBottom = 48;
  const MIN_DISTANCE = 84;
  const CARD_GAP = 24;

  eventsArr.forEach((event, index) => {
    const isLeft = index % 2 === 0;
    const h = heightsMap[event.id] || 100;

    let y = isLeft ? leftBottom : rightBottom;
    const prevNodeY = index > 0 ? pos[index - 1] : 0;

    if (index > 0) {
      y = Math.max(y, prevNodeY + MIN_DISTANCE);
    }

    pos.push(y);

    if (isLeft) {
      leftBottom = y + h + CARD_GAP;
    } else {
      rightBottom = y + h + CARD_GAP;
    }
  });

  return { positions: pos, containerHeight: Math.max(leftBottom, rightBottom) + 64 };
}

export function EventsTimeline({
  events,
  onReorder,
  entityNameMap,
  onInsert,
  onDelete,
  isDragDisabled = false
}: EventsTimelineProps) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragOrder, setDragOrder] = useState<EventSummary[]>(events);

  useEffect(() => {
    if (!activeId) {
      setDragOrder(events);
    }
  }, [events, activeId]);

  function handleDragStart(e: any) {
    setActiveId(e.active.id as string);
  }

  function handleDragOver(e: any) {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setDragOrder(prev => {
        const oldIndex = prev.findIndex(ev => ev.id === active.id);
        const newIndex = prev.findIndex(ev => ev.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          return arrayMove(prev, oldIndex, newIndex);
        }
        return prev;
      });
    }
  }

  function handleDragEnd(dragEvent: DragEndEvent) {
    setActiveId(null);
    const { active, over } = dragEvent;
    if (over && active.id !== over.id) {
      const oldIndex = events.findIndex(e => e.id === active.id);
      const newIndex = events.findIndex(e => e.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(events, oldIndex, newIndex));
      }
    } else {
      setDragOrder(events);
    }
  }

  const closestYCenter = useCallback((args: any) => {
    const { collisionRect, droppableRects, droppableContainers } = args;
    const activeCenterY = collisionRect.top + collisionRect.height / 2;

    let minDistance = Infinity;
    let closestId = null;
    let closestContainer = null;

    for (const container of droppableContainers) {
      const id = container.id;
      const rect = (droppableRects && typeof droppableRects.get === 'function')
        ? droppableRects.get(id)
        : container.rect?.current;

      if (!rect) continue;

      const containerCenterY = rect.top + rect.height / 2;
      // We explicitly ignore X distance and ONLY care about Y distance!
      const distanceY = Math.abs(activeCenterY - containerCenterY);

      if (distanceY < minDistance) {
        minDistance = distanceY;
        closestId = id;
        closestContainer = container;
      }
    }

    if (closestId != null) {
      return [{ id: closestId, data: { droppableContainer: closestContainer, value: minDistance } }];
    }
    return [];
  }, []);

  const [heights, setHeights] = useState<Record<string, number>>({});

  const handleHeightChange = useCallback((id: string, height: number) => {
    setHeights(prev => prev[id] === height ? prev : { ...prev, [id]: height });
  }, []);

  const { positions: currentPositions, containerHeight } = useMemo(() => {
    return calculatePositions(dragOrder, heights);
  }, [dragOrder, heights]);

  const { positions: originalPositions } = useMemo(() => {
    return calculatePositions(events, heights);
  }, [events, heights]);

  if (events.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body1">{t('entity_list.events.no_events')}</Typography>
      </Box>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestYCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <SortableContext items={dragOrder.map(e => e.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{ mt: '4px', pt: '4px', position: 'relative', height: containerHeight, overflowX: 'hidden' }}>
          {/* Continuous vertical spine line through center */}
          <Box sx={{
            position: 'absolute',
            top: 0, bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 3,
            bgcolor: 'primary.main',
            zIndex: 0,
          }} />

          {/* Insert point above first event */}
          <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 48, zIndex: 10 }}>
            <SpineInsertPoint insertOrder={events[0]?.order ?? 0} onInsert={onInsert} currentCount={events.length} />
          </Box>

          {/* Independent Spine Elements map perfectly onto current positions! */}
          {currentPositions.map((y, index) => (
            <Fragment key={`spine-element-${index}`}>
              <Box sx={{ position: 'absolute', left: '50%', top: y + 10, transform: 'translateX(-50%)', zIndex: 2, transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <SpineDot order={events[index]?.order ?? index} />
              </Box>
              <Box sx={{ position: 'absolute', left: '50%', top: y + 48, transform: 'translateX(-50%)', width: 48, zIndex: 2, transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <SpineInsertPoint insertOrder={(events[index]?.order ?? index) + 1} onInsert={onInsert} currentCount={events.length} />
              </Box>
            </Fragment>
          ))}

          {dragOrder.map((event, index) => {
            const isDraggingNode = event.id === activeId;

            const originalIndex = events.findIndex(e => e.id === event.id);
            const originalIsLeft = originalIndex % 2 === 0;
            const originalY = originalPositions[originalIndex];

            const currentIndex = index;
            const currentIsLeft = currentIndex % 2 === 0;
            const currentY = currentPositions[currentIndex];

            const y = isDraggingNode ? originalY : currentY;
            const isLeft = isDraggingNode ? originalIsLeft : currentIsLeft;
            const displayIndex = isDraggingNode ? originalIndex : currentIndex;

            return (
              <SortableEventNode
                key={event.id}
                event={event}
                index={displayIndex}
                y={y}
                isLeft={isLeft}
                isDraggingNode={isDraggingNode}
                entityNameMap={entityNameMap}
                onDelete={onDelete}
                onInsert={onInsert}
                handleHeightChange={handleHeightChange}
                isDragDisabled={isDragDisabled}
              />
            );
          })}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
